import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';
import { useSettings } from '@wu/widget-core';

type Autoplay = {
  enabled: boolean;
  start: () => void;
  stop: () => void;
};

type LayoutContextType = {
  widgetContainerWidth: number;
  setWidgetWidth: (containerWidth: number) => void;
  rerender?: Function;
  setRerender?: (func: Function) => void;
  autoplay?: Autoplay;
  setAutoplay?: Dispatch<SetStateAction<Autoplay | undefined>>;
};

export const LayoutContext = createContext<LayoutContextType>({
  widgetContainerWidth: 0,
  setWidgetWidth: () => {}
});

export function useLayout() {
  const context = useContext<LayoutContextType>(LayoutContext);

  if (context === null) {
    throw new Error(`useLayout should be used within LayoutProvider`);
  }
  return context;
}

export function LayoutProvider({ children }: PropsWithChildren) {
  const { widgetWidth } = useSettings();
  const [widgetContainerWidth, setWidgetContainerWidth] = useState(0);
  const [rerender, setRerender] = useState<Function | undefined>(undefined);
  const [autoplay, setAutoplay] = useState<Autoplay | undefined>(undefined);

  const setRerenderCallback = useCallback(
    (func: Function) => setRerender(() => func),
    []
  );

  const setWidgetWidth = (containerWidth: number) => {
    if (widgetWidth.auto) {
      setWidgetContainerWidth(containerWidth);
      return;
    }

    setWidgetContainerWidth(Math.min(containerWidth, widgetWidth.customValue));
  };

  return (
    <LayoutContext.Provider
      value={{
        rerender,
        setRerender: setRerenderCallback,
        autoplay,
        setAutoplay,
        widgetContainerWidth,
        setWidgetWidth
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
}
